import { useCallback, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import LoadingButton from "@atlaskit/button/loading-button";
import Heading from "@atlaskit/heading";
import { Box, xcss, Text, Grid } from "@atlaskit/primitives";

import { useScreenManager } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-screen-manager.tsx";
import { getCurrentUrlWithParams } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/utils";

import { messages } from "./messages";
import { TeamType, Redirect } from "./types";
import { getWorkOptionsByTeamType } from "./utils";

type Props = {
    teamType: TeamType;
};

type QueryParams = {
    redirect: Redirect;
    templateTitle?: string;
} | null;

const LeftHandSide = ({ teamType }: Props) => {
    const { formatMessage } = useIntl();
    const { nextScreen } = useScreenManager();
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [queryParams, setQueryParams] = useState<QueryParams>(null);
    const workOptions = getWorkOptionsByTeamType(teamType);

    const handleOptionClick = (
        optionId: string,
        redirect: Redirect,
        templateTitle?: string
    ) => {
        setSelectedOption(optionId);
        setQueryParams({ redirect, templateTitle });
    };

    const handleKeyDown = (
        e: React.KeyboardEvent,
        optionId: string,
        redirect: Redirect,
        templateTitle?: string
    ) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleOptionClick(optionId, redirect, templateTitle);
        }
    };

    const handleOnClick = useCallback(() => {
        if (queryParams) {
            const updatedUrl = getCurrentUrlWithParams({
                jtbdD0Onboarding: queryParams.redirect,
                ...(queryParams.templateTitle && {
                    jtbdD0OnboardingTemplateTitle: queryParams.templateTitle
                })
            });
            history.replaceState({}, "", updatedUrl);
            nextScreen();
        }
    }, [queryParams, nextScreen]);

    return (
        <Box xcss={wrapper}>
            <Box xcss={content}>
                <Box xcss={heading}>
                    <Heading size="large" as="h1" testId="work-on-first-header">
                        <FormattedMessage
                            {...(teamType === "default"
                                ? messages.defaultHeader
                                : messages.header)}
                        />
                    </Heading>
                    <Text
                        color="color.text.subtle"
                        testId="work-on-first-subheader"
                    >
                        <FormattedMessage
                            {...(teamType === "default"
                                ? messages.defaultSubHeader
                                : messages.subHeader)}
                        />
                    </Text>
                </Box>
                <Grid gap="space.200" xcss={grid} role="list">
                    {workOptions.map((option) => (
                        <Box
                            xcss={[
                                card,
                                selectedOption === option.id && cardSelected
                            ]}
                            key={option.id}
                            onClick={() =>
                                handleOptionClick(
                                    option.id,
                                    option.redirect,
                                    option.templateTitle
                                )
                            }
                            onKeyDown={(e: React.KeyboardEvent) =>
                                handleKeyDown(
                                    e,
                                    option.id,
                                    option.redirect,
                                    option.templateTitle
                                )
                            }
                            role="listitem"
                            tabIndex={0}
                            testId={`work-option-${option.id}`}
                        >
                            <option.icon
                                spacing="spacious"
                                color="var(--ds-icon-subtle)"
                                label={option.id}
                            />
                            <Text color="color.text" weight="bold">
                                {formatMessage(option.name)}
                            </Text>
                        </Box>
                    ))}
                </Grid>
            </Box>
            <Box xcss={button}>
                <LoadingButton
                    appearance="primary"
                    isDisabled={selectedOption === null}
                    onClick={handleOnClick}
                    testId="work-on-first-primary-cta"
                >
                    <FormattedMessage {...messages.nextButton} />
                </LoadingButton>
            </Box>
        </Box>
    );
};

const wrapper = xcss({
    display: "flex",
    flexDirection: "column",
    padding: "space.800",
    alignItems: "stretch",
    gap: "space.300",
    justifyContent: "space-between"
});

const content = xcss({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "space.400"
});

const heading = xcss({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "space.150",
    alignSelf: "stretch"
});

const grid = xcss({
    width: "100%",
    height: "236px",
    gridTemplateColumns: "1fr 1fr"
});

const card = xcss({
    "display": "flex",
    "minHeight": "32px",
    "alignItems": "center",
    "paddingInlineStart": "space.150",
    "paddingInlineEnd": "space.150",
    "gap": "space.100",
    "borderRadius": "border.radius.200",
    "border": "1px solid",
    "borderColor": "color.border",
    "background": "color.background.input",
    "cursor": "pointer",
    ":hover": {
        borderColor: "color.border.selected"
    }
});

const cardSelected = xcss({
    border: "2px solid",
    borderColor: "color.border.selected"
});

const button = xcss({
    display: "flex",
    justifyContent: "end"
});

export default LeftHandSide;
